import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { useEventCallback } from '@icp/hooks';
import { createPortal } from 'react-dom';

const SIZE = 50;
const EDGE_DISTANCE = 10; // 吸边距离
const FAULT_TOLERANCE_DISTANCE = 6; // 点击容错距离

const minmax = (min, value, max) => Math.min(Math.max(min, value), max);

/**
 * 根据吸附边和按钮当前的 left/top 像素位置，计算出语义化的 CSS 定位属性。
 * 规则：始终用"吸附边"作为主轴，用"离哪个垂直边更近"作为副轴，
 * 这样缩放浏览器时按钮不会被截断。
 *
 * @param {'left'|'right'|'top'|'bottom'} snapEdge 吸附的边
 * @param {number} pixelLeft  按钮当前距窗口左边的像素距离
 * @param {number} pixelTop   按钮当前距窗口顶边的像素距离
 * @returns {Object} CSS position 属性，如 { right: 10, bottom: 10 }
 */
function toSemanticPosition(snapEdge, pixelLeft, pixelTop) {
  const winW = window.innerWidth;
  const winH = window.innerHeight;

  if (snapEdge === 'right' || snapEdge === 'left') {
    const horizontal = snapEdge === 'right' ? { right: EDGE_DISTANCE } : { left: EDGE_DISTANCE };
    // 垂直方向：离顶部近用 top，离底部近用 bottom
    const vertical =
      pixelTop + SIZE / 2 < winH / 2
        ? { top: minmax(EDGE_DISTANCE, pixelTop, winH - SIZE - EDGE_DISTANCE) }
        : { bottom: minmax(EDGE_DISTANCE, winH - pixelTop - SIZE, winH - SIZE - EDGE_DISTANCE) };
    return { ...horizontal, ...vertical };
  }

  // snapEdge === 'top' || 'bottom'
  const vertical = snapEdge === 'bottom' ? { bottom: EDGE_DISTANCE } : { top: EDGE_DISTANCE };
  // 水平方向：离左边近用 left，离右边近用 right
  const horizontal =
    pixelLeft + SIZE / 2 < winW / 2
      ? { left: minmax(EDGE_DISTANCE, pixelLeft, winW - SIZE - EDGE_DISTANCE) }
      : { right: minmax(EDGE_DISTANCE, winW - pixelLeft - SIZE, winW - SIZE - EDGE_DISTANCE) };
  return { ...vertical, ...horizontal };
}

function FloatButton(props) {
  const { className, style, tooltip, icon, onClick: onClickProp } = props;

  const ref = useRef();
  const [dragging, setDragging] = useState(false);
  // 用语义化定位存储，默认右下角
  const [position, setPosition] = useState({ right: EDGE_DISTANCE, bottom: EDGE_DISTANCE });
  const onClick = useEventCallback(onClickProp);

  const handlers = useMemo(() => {
    // 通用的拖拽处理函数
    const handleDragStart = (clientX, clientY) => {
      setDragging(true);

      const { left: initialX, top: initialY } = ref.current.getBoundingClientRect();
      let currentX = initialX;
      let currentY = initialY;
      // 记录点击位置在按钮内部的偏移
      const clickOffset = {
        dx: clientX - initialX,
        dy: clientY - initialY,
      };

      const handleDragMove = (moveClientX, moveClientY) => {
        // 直接根据鼠标位置和偏移量计算按钮应处的位置
        const newX = moveClientX - clickOffset.dx;
        const newY = moveClientY - clickOffset.dy;
        // 限制按钮不能拖出屏幕边界
        currentX = Math.max(0, Math.min(window.innerWidth - SIZE, newX));
        currentY = Math.max(0, Math.min(window.innerHeight - SIZE, newY));
        // 拖拽过程中用 left/top 跟手，不做语义转换（避免跳动）
        setPosition({ left: currentX, top: currentY });
      };

      const handleDragEnd = () => {
        setDragging(false);
        // 移动位置足够小时视为用户点击操作
        if (
          currentX <= initialX + FAULT_TOLERANCE_DISTANCE &&
          currentX >= initialX - FAULT_TOLERANCE_DISTANCE &&
          currentY <= initialY + FAULT_TOLERANCE_DISTANCE &&
          currentY >= initialY - FAULT_TOLERANCE_DISTANCE
        ) {
          onClick();
        }

        // 松手吸边：找最近的边，然后转换为语义化定位
        const finalRect = ref.current.getBoundingClientRect();
        const distance = {
          toTop: finalRect.top,
          toBottom: window.innerHeight - finalRect.top - SIZE,
          toLeft: finalRect.left,
          toRight: window.innerWidth - finalRect.left - SIZE,
        };
        const minDistance = Math.min(
          distance.toTop,
          distance.toBottom,
          distance.toLeft,
          distance.toRight,
        );

        let snapEdge;
        switch (minDistance) {
          case distance.toLeft:
            snapEdge = 'left';
            break;
          case distance.toRight:
            snapEdge = 'right';
            break;
          case distance.toTop:
            snapEdge = 'top';
            break;
          case distance.toBottom:
            snapEdge = 'bottom';
            break;
          default:
            snapEdge = 'right';
            break;
        }

        const targetPosition = toSemanticPosition(snapEdge, currentX, currentY);

        // 如果目标定位的属性名和当前拖拽时的 left/top 不同（比如要切换到 right/bottom），
        // 需要先把当前位置用目标属性名"原地"表达一次（位置不变，只换参照边），
        // 让 transition 有同名属性可以插值，下一帧再设最终值，动画才能正常播放。
        const needsAxisSwap =
          ('right' in targetPosition && !('left' in targetPosition)) ||
          ('bottom' in targetPosition && !('top' in targetPosition));

        if (needsAxisSwap) {
          const equivalentPosition = {
            ...('right' in targetPosition && { right: window.innerWidth - currentX - SIZE }),
            ...('left' in targetPosition && { left: currentX }),
            ...('bottom' in targetPosition && { bottom: window.innerHeight - currentY - SIZE }),
            ...('top' in targetPosition && { top: currentY }),
          };
          // 先同步设置等价位置（无动画，属性名已对齐），下一帧再触发吸附动画
          setPosition(equivalentPosition);
          requestAnimationFrame(() => {
            setPosition(targetPosition);
          });
        } else {
          setPosition(targetPosition);
        }
      };

      return { handleDragMove, handleDragEnd };
    };

    // 鼠标事件处理器
    const onMouseDown = (downEvent) => {
      downEvent.preventDefault();
      downEvent.stopPropagation();

      const { handleDragMove, handleDragEnd } = handleDragStart(
        downEvent.clientX,
        downEvent.clientY,
      );

      const onMouseMove = (moveEvent) => {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        handleDragMove(moveEvent.clientX, moveEvent.clientY);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        handleDragEnd();
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    // 触摸事件处理器
    const onTouchStart = (downEvent) => {
      downEvent.preventDefault();
      downEvent.stopPropagation();

      const touch = downEvent.touches[0];
      const { handleDragMove, handleDragEnd } = handleDragStart(touch.clientX, touch.clientY);

      const onTouchMove = (moveEvent) => {
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        const moveTouch = moveEvent.touches[0];
        if (moveTouch) {
          handleDragMove(moveTouch.clientX, moveTouch.clientY);
        }
      };

      const onTouchEnd = () => {
        document.removeEventListener('touchmove', onTouchMove);
        document.removeEventListener('touchend', onTouchEnd);
        handleDragEnd();
      };

      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd);
    };

    return { onMouseDown, onTouchStart };
  }, [onClick]);

  return createPortal(
    <Tooltip title={tooltip}>
      <div
        ref={ref}
        className={className}
        style={{
          ...style,
          ...position,
          width: SIZE,
          height: SIZE,
          position: 'fixed',
          zIndex: 3,
          cursor: dragging ? 'grabbing' : 'pointer',
          ...(!dragging && { transition: 'all 0.2s ease-in-out' }),
        }}
        onMouseDown={handlers.onMouseDown}
        onTouchStart={handlers.onTouchStart}
      >
        {icon}
      </div>
    </Tooltip>,
    document.body,
  );
}

FloatButton.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  tooltip: PropTypes.string,
  icon: PropTypes.node,
  onClick: PropTypes.func,
};

export default FloatButton;
